import dayjs from "dayjs";

/**
 * Returns the number of whole calendar days remaining between `now` and
 * `expirationDate`. Returns 0 if the expiration date is today or in the past.
 *
 * We use `diff` with `"day"` (which truncates fractions) so that an
 * expiration at 23:59 tonight is still "today" (0 days) and an expiration
 * at 00:01 tomorrow is "1 day".
 */
export const trialDaysRemaining = (
  expirationDate: string,
  now: Date = new Date(),
): number => {
  const expiry = dayjs(expirationDate).startOf("day");
  const today = dayjs(now).startOf("day");
  const diff = expiry.diff(today, "day");
  return Math.max(0, diff);
};

/**
 * Returns a human-readable label for the trial period, e.g.:
 * - "Premium Trial (ends today)"
 * - "Premium Trial (1 day left)"
 * - "Premium Trial (3 days left)"
 *
 * Returns `null` when the period is not a trial/intro period — the caller
 * should fall back to the regular amount subtitle.
 */
export const formatTrialLabel = (
  periodType: string,
  expirationDate: string | null,
  now: Date = new Date(),
): string | null => {
  if (periodType !== "TRIAL" && periodType !== "INTRO") {
    return null;
  }

  if (!expirationDate) {
    // Trial with no known expiry — just show "Premium Trial" without countdown.
    return "Premium Trial";
  }

  const days = trialDaysRemaining(expirationDate, now);

  if (days === 0) {
    return "Premium Trial (ends today)";
  }

  if (days === 1) {
    return "Premium Trial (1 day left)";
  }

  return `Premium Trial (${days} days left)`;
};
